import React, { FC, forwardRef, useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale/en-GB';
import 'react-datepicker/dist/react-datepicker.css';
import {
  AsteriskOutlinedIcon,
  ArrowRightIcon,
  BackIcon,
  CloseIcon,
  DateArrowDownIcon,
  DateArrowUpIcon,
} from '@src/svg';
import { Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { addMonths, Day, format, isValid, parse } from 'date-fns';
import { ControlledDateSelectPropsType } from '@uikit/FormItems/constants';
import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';
import { useOutsideClick } from '@hooks/useOutsideClick';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';

const MIN_YEAR_INTERVAL = 110;
const MAX_YEAR_INTERVAL = 10;

const getMinMaxYear = (currentYear: number) => {
  return {
    minYear: currentYear - MIN_YEAR_INTERVAL,
    maxYear: currentYear + MAX_YEAR_INTERVAL,
  };
};

const parseDateInputValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value !== 'string' || !value) {
    return null;
  }

  const parsedDate = parse(value, 'MM/dd/yyyy', new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

interface DateRange {
  startDate: any;
  endDate: any;
}

const customEnGB = {
  ...enGB,
  options: {
    ...enGB.options,
    weekStartsOn: 0 as Day,
  },
};

registerLocale('custom-enGB', customEnGB);
setDefaultLocale('custom-enGB');

type InputMaskProps = React.ComponentProps<typeof InputMask>;

const MaskedInput = forwardRef<HTMLInputElement, InputMaskProps>(
  (props, ref) => {
    return <InputMask {...props} inputRef={ref as any} />;
  }
);

MaskedInput.displayName = 'MaskedInput';

export const ControlledDateSelect: FC<ControlledDateSelectPropsType> = ({
  name,
  control,
  title,
  placeholder,
  disabled,
  info,
  IconLeft,
  IconRight,
  maxWidth,
  rules,
  minDate,
  maxDate,
  onChange,
  trigger,
  setError,
  selectsRange,
  onePlaceHolder,
  dataTestId,
  className = '',
  autoPopulateTwelveMonthEnd = false,
  portalId,
  popperPlacement = 'bottom-start',
}) => {
  const yearRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear()
  );
  const yearSelectRef = useRef<HTMLDivElement>(null);
  const monthSelectRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<any>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const rangeStartInputRef = useRef<HTMLInputElement | null>(null);
  const rangeEndInputRef = useRef<HTMLInputElement | null>(null);
  const isRangeTypingRef = useRef(false);
  const rangeTypingIdleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(rangeTypingIdleTimeoutRef.current);
    };
  }, []);

  const [isYearOpen, setIsYearOpen] = useState<boolean>(false);
  const [isMonthOpen, setIsMonthOpen] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [activeRangeDate, setActiveRangeDate] = useState<Date | null>(null);
  const activeRangeFieldRef = useRef<'start' | 'end'>('start');
  const [isOpen, setIsOpen] = useState(false);

  const forceFocus = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (selectsRange) {
          const target =
            activeRangeFieldRef.current === 'end'
              ? rangeEndInputRef.current
              : rangeStartInputRef.current;
          target?.focus();
        } else {
          inputRef.current?.focus();
        }
      }, 0);
    });
  };

  const focusRangeStartInput = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        rangeStartInputRef.current?.focus();
      }, 0);
    });
  };

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (!isOpen) return;

      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.closest('.react-datepicker') ||
          target.closest('.year-select-options-wrapper') ||
          target.closest('.month-select-wrapper'))
      ) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const value = control._getWatch(name);
    let year = '';
    if (selectsRange && Array.isArray(value)) {
      year = value[0]?.split('/')?.[2] || '';
    } else if (typeof value === 'string') {
      year = value.split('/')?.[2] || '';
    }
    if (year && !isNaN(Number(year))) {
      setCurrentYear(Number(year));
    }
  }, [control, name, selectsRange]);

  const { startDate, endDate } = dateRange;

  const handleCloseDropdowns = () => {
    setIsYearOpen(false);
    setIsMonthOpen(false);
  };

  useOutsideClick({
    ref: yearSelectRef,
    onOutsideClick: handleCloseDropdowns,
  });
  useOutsideClick({
    ref: monthSelectRef,
    onOutsideClick: handleCloseDropdowns,
  });

  const years = Array.from(
    { length: MIN_YEAR_INTERVAL + MAX_YEAR_INTERVAL + 1 },
    (_, i) => new Date().getFullYear() - MIN_YEAR_INTERVAL + i
  );

  const isValidDateFormat = (value: string): boolean => {
    const regex = /^(0[1-9]|1[0-2])\/([0-2][0-9]|3[01])\/\d{4}$/;
    return regex.test(value);
  };

  const validateDate = (value: string | null): undefined | true => {
    if (value) {
      if (!isValidDateFormat(value)) {
        return;
      }
      const dateValue = parse(value, 'MM/dd/yyyy', new Date());
      if (!isValid(dateValue)) {
        return;
      }
      if (minDate && dateValue < minDate) {
        return;
      }
      if (maxDate && dateValue > maxDate) {
        return;
      }
    }
    return true;
  };

  const handleValidateInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    let value = e.target.value.trim();
    const digitsOnly = value.replace(/\D/g, '');

    if (!digitsOnly.length) {
      field.onChange('');
      return;
    }

    value = value.replace(/_/g, '');

    if (Boolean(validateDate(value)) && value.length === 10) {
      field.onChange(value);
      field.onBlur();
      return;
    }

    if (!Boolean(validateDate(value))) {
      setError &&
        setError(name, {
          type: 'manual',
          message: 'Invalid date format',
        });
      return;
    }

    let dateValue = parse(value, 'MM/dd/yyyy', new Date());

    const { minYear: minDropdownYear, maxYear: maxDropdownYear } =
      getMinMaxYear(currentYear);

    if (!isValid(dateValue)) return;

    if (dateValue.getFullYear() > maxDropdownYear) {
      dateValue.setFullYear(maxDropdownYear);
    } else if (dateValue.getFullYear() < minDropdownYear) {
      dateValue.setFullYear(minDropdownYear);
    }

    const formattedDate = format(dateValue, 'MM/dd/yyyy');
    field.onChange(formattedDate);
  };

  const handleValidateRangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any,
    position: 'start' | 'end'
  ) => {
    const rawValue = e.target.value.trim();
    const digitsOnly = rawValue.replace(/\D/g, '');
    const currentValue = Array.isArray(field.value) ? field.value : ['', ''];
    const otherRaw =
      (position === 'start' ? currentValue[1] : currentValue[0]) || '';
    const thisRaw =
      (position === 'start' ? currentValue[0] : currentValue[1]) || '';

    if (rawValue === thisRaw) {
      return;
    }

    const thisRawDigitsOnly = thisRaw.replace(/\D/g, '');
    const isDeleting = digitsOnly.length < thisRawDigitsOnly.length;

    if (position === 'end' && !otherRaw && digitsOnly.length && !isDeleting) {
      focusRangeStartInput();
      return;
    }

    if (!digitsOnly.length) {
      const cleared = position === 'start' ? ['', otherRaw] : [otherRaw, ''];
      field.onChange(cleared);

      const remainingDate = parseDateInputValue(otherRaw);
      const newStartDate = position === 'start' ? null : remainingDate;
      const newEndDate = position === 'end' ? null : remainingDate;
      setDateRange({ startDate: newStartDate, endDate: newEndDate });
      setActiveRangeDate(newStartDate || newEndDate || null);
      return;
    }

    isRangeTypingRef.current = true;
    clearTimeout(rangeTypingIdleTimeoutRef.current);
    rangeTypingIdleTimeoutRef.current = setTimeout(() => {
      isRangeTypingRef.current = false;
    }, 1500);

    const partialRange =
      position === 'start' ? [rawValue, otherRaw] : [otherRaw, rawValue];
    field.onChange(partialRange);

    if (digitsOnly.length < 8) return;

    const yearStr = digitsOnly.slice(4, 8);
    const typedYear = parseInt(yearStr, 10);
    const clampedMonth = Math.min(
      Math.max(parseInt(digitsOnly.slice(0, 2), 10), 1),
      12
    );
    const daysInClampedMonth = new Date(typedYear, clampedMonth, 0).getDate();
    const clampedDay = Math.min(
      Math.max(parseInt(digitsOnly.slice(2, 4), 10), 1),
      daysInClampedMonth
    );

    const value = `${String(clampedMonth).padStart(2, '0')}/${String(
      clampedDay
    ).padStart(2, '0')}/${yearStr}`;
    const dateValue = parse(value, 'MM/dd/yyyy', new Date());

    if (position === 'start') {
      let endRaw = otherRaw;
      let endDateValue = endRaw ? parseDateInputValue(endRaw) : null;

      if (!endRaw && autoPopulateTwelveMonthEnd) {
        const autoEnd = addMonths(dateValue, 12);
        endDateValue =
          adjustedMaxDate && autoEnd > adjustedMaxDate
            ? adjustedMaxDate
            : autoEnd;
        endRaw = format(endDateValue, 'MM/dd/yyyy');
      }

      setDateRange({ startDate: dateValue, endDate: endDateValue });
      setActiveRangeDate(endDateValue || dateValue);
      setCurrentYear(dateValue.getFullYear());

      const newRange = [value, endRaw];
      field.onChange(newRange);

      if (endRaw && Boolean(validateDate(endRaw))) {
        onChange?.(newRange);
        field.onBlur();
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          const endInput = rangeEndInputRef.current;
          if (endInput && document.activeElement !== endInput) {
            endInput.focus();
            endInput.setSelectionRange(0, 0);
          }
        }, 0);
      });
    } else {
      setDateRange((prev) => ({ ...prev, endDate: dateValue }));
      setActiveRangeDate(dateValue);

      const newRange = [otherRaw, value];
      field.onChange(newRange);
      // Only call the backend once both ends of the range are filled in.
      if (otherRaw && Boolean(validateDate(otherRaw))) {
        onChange?.(newRange);
        field.onBlur();
      }
      forceFocus();
    }
  };
  const { minYear, maxYear } = getMinMaxYear(currentYear);
  const minBoundDate = new Date(minYear, 0, 1);
  const maxBoundDate = new Date(maxYear, 11, 31);

  const adjustedMinDate = minDate
    ? minDate < minBoundDate
      ? minBoundDate
      : minDate
    : minBoundDate;

  const adjustedMaxDate = maxDate
    ? maxDate > maxBoundDate
      ? maxBoundDate
      : maxDate
    : maxBoundDate;

  useEffect(() => {
    if (isYearOpen && yearRefs.current[currentYear]) {
      yearRefs.current[currentYear]?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [isYearOpen, currentYear]);

  return (
    <div
      style={{ maxWidth }}
      className="input-text"
      ref={datePickerRef}
      data-test-id={`${dataTestId}-controlled-date-select`}
    >
      {info?.toggle && (
        <InfoTooltip className="info_shower" title={info?.desc}>
          <div></div>
        </InfoTooltip>
      )}

      <div
        className={!disabled ? '' : 'disabled'}
        data-test-id={`${dataTestId}-controlled-date-select-container`}
      >
        <div
          className="input-title-wrapper"
          data-test-id={`${dataTestId}-controlled-date-select-title-wrapper`}
        >
          {title && (
            <span className="input-title">
              {title}
              {!!rules?.required && (
                <span className="input-required-marker" aria-hidden="true">
                  <AsteriskOutlinedIcon
                    width={10}
                    height={10}
                    color="#fb6403"
                  />
                </span>
              )}
            </span>
          )}
        </div>

        <Controller
          key={name}
          control={control}
          name={name}
          rules={{
            ...rules,
            validate: (value, formValues) => {
              const dateValidation = validateDate(value);
              const customValidation =
                typeof rules?.validate === 'function'
                  ? rules.validate(value, formValues)
                  : true;
              return dateValidation === true
                ? customValidation
                : dateValidation;
            },
          }}
          render={({ field, fieldState }) => {
            const isInvalid = rules?.validate
              ? !(rules?.validate as unknown as any)?.() || fieldState.invalid
              : false;

            const validationDynamicClassName = getDynamicValidationColorsGlobal(
              field,
              isInvalid
            );
            const fieldStartDate =
              selectsRange && Array.isArray(field.value)
                ? parseDateInputValue(field.value[0])
                : null;
            const fieldEndDate =
              selectsRange && Array.isArray(field.value)
                ? parseDateInputValue(field.value[1])
                : null;
            const rangeStartDate = startDate || fieldStartDate;
            const rangeEndDate = endDate || fieldEndDate;

            return (
              <span
                className={`input ${validationDynamicClassName} ${
                  field.value && className === 'custom_date_part_input_active'
                    ? className
                    : ''
                }`}
                data-test-id={`${dataTestId}-controlled-date-select-input`}
              >
                {IconLeft && <span>{IconLeft}</span>}
                <DatePicker
                  ref={pickerRef}
                  locale="custom-enGB"
                  id={name}
                  shouldCloseOnSelect
                  placeholderText={placeholder}
                  disabled={disabled}
                  popperPlacement={popperPlacement}
                  preventOpenOnFocus
                  withPortal={!portalId}
                  portalId={portalId}
                  onFocus={() => trigger?.(name)}
                  onCalendarOpen={() => setIsOpen(true)}
                  onCalendarClose={() => {
                    setIsOpen(false);
                    forceFocus();
                    // Don't let a stale 'end' selection from a prior
                    // interaction hijack the next calendar-icon reopen —
                    // default back to editing the start date unless the
                    // user explicitly focuses the end input again.
                    activeRangeFieldRef.current = 'start';
                  }}
                  selected={
                    selectsRange
                      ? activeRangeDate || rangeStartDate
                      : field.value && typeof field.value === 'string'
                        ? parse(field.value, 'MM/dd/yyyy', new Date())
                        : null
                  }
                  dayClassName={(d: Date) => {
                    const hasValue = selectsRange
                      ? Array.isArray(field.value) &&
                        (field.value[0] || field.value[1])
                      : Boolean(field.value);

                    if (!hasValue) {
                      const today = new Date();
                      return d.getDate() === today.getDate() &&
                        d.getMonth() === today.getMonth()
                        ? 'react-datepicker__day--today'
                        : '';
                    }
                    return '';
                  }}
                  openToDate={
                    selectsRange
                      ? activeRangeDate || rangeStartDate || rangeEndDate
                      : undefined
                  }
                  startDate={selectsRange ? rangeStartDate : null}
                  endDate={selectsRange ? rangeEndDate : null}
                  selectsRange={selectsRange}
                  onChange={(
                    date: Date | [Date | null, Date | null] | null
                  ) => {
                    if (selectsRange) {
                      let [start, rawEnd] = date as [Date | null, Date | null];

                      if (isRangeTypingRef.current) return;
                      if (!start && !rawEnd) return;

                      const wasRangeFilled = Boolean(
                        rangeStartDate && rangeEndDate
                      );
                      let didOverrideReset = false;
                      if (wasRangeFilled && start && !rawEnd) {
                        const clickedDate = start;
                        didOverrideReset = true;
                        if (activeRangeFieldRef.current === 'end') {
                          if (clickedDate < rangeStartDate) {
                            start = clickedDate;
                            rawEnd = rangeStartDate;
                          } else {
                            start = rangeStartDate;
                            rawEnd = clickedDate;
                          }
                        } else if (clickedDate > rangeEndDate) {
                          start = clickedDate;
                          rawEnd = null;
                        } else {
                          start = clickedDate;
                          rawEnd = rangeEndDate;
                        }
                      }

                      let end = rawEnd;
                      if (autoPopulateTwelveMonthEnd && start && !end) {
                        const autoEnd = addMonths(start, 12);
                        end =
                          adjustedMaxDate && autoEnd > adjustedMaxDate
                            ? adjustedMaxDate
                            : autoEnd;
                      }
                      setDateRange({ startDate: start, endDate: end });
                      setActiveRangeDate(end || start);
                      const formattedRange = [
                        start ? format(start, 'MM/dd/yyyy') : '',
                        end ? format(end, 'MM/dd/yyyy') : '',
                      ];
                      field.onChange(formattedRange);
                      onChange?.(formattedRange);

                      if (formattedRange[1]) {
                        forceFocus();
                        if (didOverrideReset) {
                          pickerRef.current?.setOpen(false);
                        }
                      }
                    } else {
                      const formattedDate = date
                        ? format(date as Date, 'MM/dd/yyyy')
                        : '';
                      setCurrentYear(
                        date
                          ? (date as Date).getFullYear()
                          : new Date().getFullYear()
                      );
                      field.onChange(formattedDate);
                      onChange?.(formattedDate);

                      forceFocus();
                    }
                  }}
                  minDate={adjustedMinDate}
                  maxDate={adjustedMaxDate}
                  renderCustomHeader={({
                    date,
                    changeYear,
                    changeMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => {
                    const currentMonth = date.getMonth();
                    const currentYear = date.getFullYear();

                    const handleDecreaseMonth = () => {
                      const newMonth = currentMonth - 1;
                      if (newMonth < 0) {
                        changeMonth(11);
                        changeYear(currentYear - 1);
                      } else {
                        changeMonth(newMonth);
                        changeYear(currentYear);
                      }
                    };

                    const handleIncreaseMonth = () => {
                      const newMonth = currentMonth + 1;
                      if (newMonth > 11) {
                        changeMonth(0);
                        changeYear(currentYear + 1);
                      } else {
                        changeMonth(newMonth);
                        changeYear(currentYear);
                      }
                    };

                    return (
                      <div
                        className="datepicker-header"
                        data-test-id={`${dataTestId}-controlled-date-select-header`}
                      >
                        <div className="datepicker-features-wrapper">
                          <div
                            className="year-select-wrapper"
                            data-test-id={`${dataTestId}-controlled-date-select-year-select-wrapper`}
                          >
                            <div
                              className="year-selected-year"
                              data-test-id={`${dataTestId}-controlled-date-select-year-selected-year`}
                              onClick={() => {
                                setIsYearOpen(!isYearOpen);
                                setIsMonthOpen(false);
                              }}
                            >
                              <div className="datepicker-choose-year-wrapper">
                                <button
                                  data-test-id={`${dataTestId}-controlled-date-select-year-dropdown-icon`}
                                  type="button"
                                  className="year-dropdown-icon"
                                >
                                  {isYearOpen ? (
                                    <DateArrowUpIcon />
                                  ) : (
                                    <DateArrowDownIcon />
                                  )}
                                </button>
                                <span className="datepicker-choosen-year">
                                  {currentYear}
                                </span>
                              </div>
                            </div>
                            {isYearOpen && (
                              <div
                                className="year-select-options-wrapper"
                                data-test-id={`${dataTestId}-select-options-wrapper`}
                                ref={yearSelectRef}
                                style={{ maxHeight: 200, overflowY: 'auto' }}
                              >
                                {years.map((year) => (
                                  <div
                                    data-test-id={`${dataTestId}-select-option-${year}`}
                                    key={year}
                                    ref={(el) => (yearRefs.current[year] = el)}
                                    className={`cursor-pointer p-2 hover:bg-gray-200 ${
                                      year === currentYear
                                        ? 'current-year-date'
                                        : ''
                                    }`}
                                    onClick={() => {
                                      changeYear(Number(year));
                                      setCurrentYear(year);
                                      setIsYearOpen(false);
                                    }}
                                  >
                                    {year}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="datepicker-rest-actions">
                            <button
                              data-test-id={`${dataTestId}-controlled-date-select-prev-button`}
                              className="prev-button"
                              onClick={handleDecreaseMonth}
                              disabled={prevMonthButtonDisabled}
                              type="button"
                            >
                              <BackIcon variant="date" />
                            </button>

                            <div className="month-select-wrapper">
                              <div
                                data-test-id={`${dataTestId}-select-month-selected-month`}
                                className="year-selected-month"
                                onClick={() => {
                                  setIsMonthOpen(!isMonthOpen);
                                  setIsYearOpen(false);
                                }}
                              >
                                {new Date(2020, currentMonth, 1).toLocaleString(
                                  'default',
                                  { month: 'long' }
                                )}
                              </div>
                              {isMonthOpen && (
                                <div
                                  className="year-select-options-wrapper"
                                  ref={monthSelectRef}
                                >
                                  {Array.from({ length: 12 }, (_, i) => (
                                    <div
                                      data-test-id={`${dataTestId}-select-option-${i}`}
                                      key={i}
                                      onClick={() => {
                                        changeMonth(i);
                                        changeYear(currentYear);
                                        setIsMonthOpen(false);
                                      }}
                                    >
                                      {new Date(2020, i, 1).toLocaleString(
                                        'default',
                                        {
                                          month: 'long',
                                        }
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <button
                              data-test-id={`${dataTestId}-controlled-date-select-next-button`}
                              className="next-button"
                              onClick={handleIncreaseMonth}
                              disabled={nextMonthButtonDisabled}
                              type="button"
                            >
                              <ArrowRightIcon variant="date" />
                            </button>
                          </div>

                          <div
                            data-test-id={`${dataTestId}-controlled-date-select-close-button`}
                            className="datepicker-close-button"
                            onClick={() => {
                              pickerRef.current.setOpen(false);
                            }}
                            style={{ marginRight: '12px', cursor: 'pointer' }}
                          >
                            <CloseIcon />
                          </div>
                        </div>
                      </div>
                    );
                  }}
                  onBlur={() => {
                    field.onBlur();
                    trigger?.(name);
                    setIsMonthOpen(false);
                    setIsYearOpen(false);
                  }}
                  customInput={
                    !selectsRange ? (
                      <MaskedInput
                        data-test-id={`${dataTestId}-select-input-mask-date`}
                        ref={inputRef}
                        mask="99/99/9999"
                        value={field.value || ''}
                        placeholder={placeholder}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleValidateInput(e, field)
                        }
                        onBlur={field.onBlur}
                      />
                    ) : (
                      <div
                        data-test-id={`${dataTestId}-select-input-mask-wrapper`}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '190px',
                          gap: '5px',
                        }}
                      >
                        <InputMask
                          inputRef={(el: HTMLInputElement | null) => {
                            rangeStartInputRef.current = el;
                          }}
                          data-test-id={`${dataTestId}-select-input-mask-start`}
                          mask="99/99/9999"
                          value={
                            Array.isArray(field.value) ? field.value?.[0] : ''
                          }
                          placeholder={placeholder}
                          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                            e.currentTarget.focus();
                            activeRangeFieldRef.current = 'start';
                            setActiveRangeDate(rangeStartDate || null);
                          }}
                          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            e.stopPropagation();
                            activeRangeFieldRef.current = 'start';
                            setActiveRangeDate(rangeStartDate || null);
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            e.stopPropagation();
                            handleValidateRangeInput(e, field, 'start');
                          }}
                          onBlur={field.onBlur}
                        />
                        {Array.isArray(field.value) && field.value[0] && (
                          <span className="date-range-separator"> - </span>
                        )}
                        <InputMask
                          inputRef={(el: HTMLInputElement | null) => {
                            rangeEndInputRef.current = el;
                          }}
                          data-test-id={`${dataTestId}-select-input-mask-end`}
                          mask="99/99/9999"
                          value={
                            Array.isArray(field.value) ? field.value?.[1] : ''
                          }
                          placeholder={onePlaceHolder ? '' : placeholder}
                          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                            if (!rangeStartDate && !rangeEndDate) {
                              activeRangeFieldRef.current = 'start';
                              setActiveRangeDate(null);
                              focusRangeStartInput();
                              return;
                            }

                            e.currentTarget.focus();
                            activeRangeFieldRef.current = 'end';
                            setActiveRangeDate(rangeEndDate || null);
                          }}
                          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            e.stopPropagation();
                            if (!rangeStartDate && !rangeEndDate) {
                              activeRangeFieldRef.current = 'start';
                              setActiveRangeDate(null);
                              focusRangeStartInput();
                              return;
                            }
                            activeRangeFieldRef.current = 'end';
                            setActiveRangeDate(rangeEndDate || null);
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            e.stopPropagation();
                            handleValidateRangeInput(e, field, 'end');
                          }}
                          onBlur={field.onBlur}
                        />
                      </div>
                    )
                  }
                />
                {IconRight && (
                  <span
                    onClick={() =>
                      !disabled && pickerRef.current?.setOpen(true)
                    }
                    style={{ cursor: disabled ? 'default' : 'pointer' }}
                  >
                    {IconRight}
                  </span>
                )}
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};
